import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { useCart } from '../../context/CartContext';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getCartCount } = useCart();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isCartPreviewOpen, setIsCartPreviewOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const cartCount = getCartCount();

  const categoryRef = useRef(null);
  const cartPreviewRef = useRef(null);

  const navigationItems = [
    { label: 'Home', path: '/homepage', icon: 'Home' },
    { label: 'Products', path: '/product-catalog', icon: 'Package', hasDropdown: true },
    { label: 'Calculator', path: '/peptide-calculator', icon: 'Calculator' },
    { label: 'Contact', path: '/contact', icon: 'Mail' },
  ];

  const categories = [
    { name: 'Performance Enhancement', count: 24, path: '/product-catalog?category=performance' },
    { name: 'Recovery & Healing', count: 18, path: '/product-catalog?category=recovery' },
    { name: 'Cognitive Enhancement', count: 15, path: '/product-catalog?category=cognitive' },
  ];

  const cartItems = [
    { id: 1, name: 'BPC-157 5mg', price: '$49.99', image: '/assets/images/peptide-1.jpg' },
    { id: 2, name: 'TB-500 5mg', price: '$59.99', image: '/assets/images/peptide-2.jpg' },
    { id: 3, name: 'Ipamorelin 5mg', price: '$44.99', image: '/assets/images/peptide-3.jpg' },
  ];

  const searchSuggestions = [
    { title: 'BPC-157', category: 'Performance Enhancement' },
    { title: 'TB-500', category: 'Recovery & Healing' },
    { title: 'CJC-1295', category: 'Performance Enhancement' },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryRef?.current && !categoryRef?.current?.contains(event?.target)) {
        setIsCategoryOpen(false);
      }
      if (cartPreviewRef?.current && !cartPreviewRef?.current?.contains(event?.target)) {
        setIsCartPreviewOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen || isSearchOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen, isSearchOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e?.key === 'Escape') {
        setIsSearchOpen(false);
        setIsMobileMenuOpen(false);
        setIsCategoryOpen(false);
        setIsCartPreviewOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
    setIsCategoryOpen(false);
  };

  const handleSearch = (e) => {
    e?.preventDefault();
    if (searchQuery?.trim()) {
      navigate(`/product-catalog?search=${encodeURIComponent(searchQuery)}`);
      setIsSearchOpen(false);
      setSearchQuery('');
    }
  };

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  const calculateCartTotal = () => {
    return cartItems?.reduce((total, item) => {
      const price = parseFloat(item?.price?.replace('$', ''));
      return total + price;
    }, 0)?.toFixed(2);
  };

  const handleCartClick = () => {
    navigate('/shopping-cart');
  };

  return (
    <>
      <header className="nav-header">
        <div className="nav-container">
          <div className="nav-logo" onClick={() => handleNavigation('/homepage')}>
            <div className="nav-logo-icon">
              <img 
                src="/assets/images/Copy_of_Black_White_Modern_Handwritten_Square_Studio_Logo-removebg-preview-1770507171149.png" 
                alt="INF LABS Logo" 
                className="w-6 h-6 object-contain"
              />
            </div>
            <span className="nav-logo-text">INF LABS</span>
          </div>

          <nav className="nav-menu">
            {navigationItems?.map((item) => (
              <div key={item?.path} className="relative" ref={item?.hasDropdown ? categoryRef : null}>
                <div
                  className={`nav-item ${isActivePath(item?.path) ? 'active' : ''}`}
                  onClick={() => {
                    if (item?.hasDropdown) {
                      setIsCategoryOpen(!isCategoryOpen);
                    } else {
                      handleNavigation(item?.path);
                    }
                  }}
                >
                  {item?.label}
                  {item?.hasDropdown && (
                    <Icon 
                      name={isCategoryOpen ? "ChevronUp" : "ChevronDown"} 
                      size={16} 
                      className="inline-block ml-1"
                    />
                  )}
                </div>

                {item?.hasDropdown && (
                  <div className={`category-dropdown ${isCategoryOpen ? 'open' : ''}`}>
                    {categories?.map((category) => (
                      <div
                        key={category?.name}
                        className="category-item"
                        onClick={() => handleNavigation(category?.path)}
                      >
                        <div className="category-item-title">{category?.name}</div>
                        <div className="category-item-count">{category?.count} products</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="nav-actions">
            <div className="nav-search-trigger" onClick={() => setIsSearchOpen(true)}>
              <Icon name="Search" size={20} color="var(--color-primary)" />
            </div>

            {/* Cart Icon */}
            <button
              onClick={handleCartClick}
              className="relative p-2 rounded-lg hover:bg-muted/50 transition-all duration-250 group"
              aria-label="Shopping cart"
            >
              <Icon name="ShoppingCart" size={22} color="var(--color-foreground)" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>

            <div className="relative" ref={cartPreviewRef}>
              <div className={`cart-preview ${isCartPreviewOpen ? 'open' : ''}`}>
                <div className="cart-preview-header">
                  <h3 className="cart-preview-title">Shopping Cart ({cartCount})</h3>
                </div>
                <div className="cart-preview-items">
                  {cartItems?.map((item) => (
                    <div key={item?.id} className="cart-preview-item">
                      <img src={item?.image} alt={item?.name} className="cart-preview-item-image" />
                      <div className="cart-preview-item-info">
                        <div className="cart-preview-item-name">{item?.name}</div>
                        <div className="cart-preview-item-price">{item?.price}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="cart-preview-footer">
                  <div className="cart-preview-total">
                    <span className="cart-preview-total-label">Total:</span>
                    <span className="cart-preview-total-value">${calculateCartTotal()}</span>
                  </div>
                  <Button 
                    variant="default" 
                    fullWidth
                    onClick={() => {
                      handleNavigation('/cart');
                      setIsCartPreviewOpen(false);
                    }}
                  >
                    View Cart
                  </Button>
                </div>
              </div>
            </div>

            <div className="nav-mobile-toggle" onClick={() => setIsMobileMenuOpen(true)}>
              <Icon name="Menu" size={24} color="var(--color-primary)" />
            </div>
          </div>
        </div>
      </header>
      <div className={`search-overlay ${isSearchOpen ? 'open' : ''}`} onClick={() => setIsSearchOpen(false)}>
        <div className="search-container" onClick={(e) => e?.stopPropagation()}>
          <form onSubmit={handleSearch}>
            <div className="search-input-wrapper">
              <input
                type="text"
                className="search-input"
                placeholder="Search peptides by name or category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                autoFocus
              />
              <div className="search-icon">
                <Icon name="Search" size={20} />
              </div>
            </div>
          </form>

          {searchQuery?.length > 0 && (
            <div className="search-suggestions">
              {searchSuggestions?.filter(item => 
                  item?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                  item?.category?.toLowerCase()?.includes(searchQuery?.toLowerCase())
                )?.map((suggestion, index) => (
                  <div
                    key={index}
                    className="search-suggestion-item"
                    onClick={() => {
                      setSearchQuery(suggestion?.title);
                      handleSearch({ preventDefault: () => {} });
                    }}
                  >
                    <div className="search-suggestion-title">{suggestion?.title}</div>
                    <div className="search-suggestion-category">{suggestion?.category}</div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
      <div className={`nav-mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="nav-mobile-header">
          <div className="nav-logo">
            <div className="nav-logo-icon">
              <Icon name="Dna" size={24} color="#FFFFFF" />
            </div>
            <span className="nav-logo-text">PeptideHub</span>
          </div>
          <div className="nav-mobile-close" onClick={() => setIsMobileMenuOpen(false)}>
            <Icon name="X" size={24} color="var(--color-error)" />
          </div>
        </div>

        <div className="nav-mobile-items">
          {navigationItems?.map((item) => (
            <div
              key={item?.path}
              className={`nav-mobile-item ${isActivePath(item?.path) ? 'active' : ''}`}
              onClick={() => handleNavigation(item?.path)}
            >
              <Icon name={item?.icon} size={20} className="inline-block mr-3" />
              {item?.label}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default Header;